import React from 'react';
import {Avatar} from '@mui/material';
import {getAwpBackendOrigin} from '../config/apiBase';
import {resolveExpertAvatarUrl} from '../utils/expertAvatar';

function avatarInitials(user, userId) {
    const display = user?.displayName
        || [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim();
    if (display) {
        const parts = display.trim().split(/\s+/).filter(Boolean);
        if (parts.length >= 2) {
            return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
        }
        return parts[0][0].toUpperCase();
    }
    const email = user?.email;
    if (email) return email[0].toUpperCase();
    if (userId) return userId[0].toUpperCase();
    return '?';
}


function toAbsoluteUrl(url, origin) {
    if (!url) return null;
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    if (url.startsWith('/')) return `${origin}${url}`;
    return url;
}

function resolveUserAvatarFetchUrl(staticSrc, id, origin, hasAvatar) {
    if (staticSrc && staticSrc.includes('/api/users/') && staticSrc.includes('/avatar')) {
        return toAbsoluteUrl(staticSrc, origin);
    }
    if (id && hasAvatar === true) {
        return `${origin}/api/users/${encodeURIComponent(id)}/avatar`;
    }
    return null;
}

/**
 * User/expert avatar with initials fallback.
 * - Expert/public `/api/experts/.../avatar` URLs load directly via img src (same-origin proxy).
 * - User `/api/users/.../avatar` URLs load via fetch+blob so JWT can be sent.
 */
export default function UserAvatar({user, userId, sx, ...avatarProps}) {
    const id = user?.id || user?.userId || user?.uid || userId;
    const [blobUrl, setBlobUrl] = React.useState(null);
    const [imgFailed, setImgFailed] = React.useState(false);

    const staticSrc = resolveExpertAvatarUrl(
        user ? {id, userId: id, uid: id, ...user} : {id, userId: id, uid: id}
    );
    const origin = getAwpBackendOrigin();
    const fetchUrl = resolveUserAvatarFetchUrl(staticSrc, id, origin, user?.hasAvatar === true);
    const needsBlobFetch = Boolean(fetchUrl);

    React.useEffect(() => {
        setImgFailed(false);
        setBlobUrl(null);
    }, [id, staticSrc, fetchUrl]);

    React.useEffect(() => {
        if (!needsBlobFetch || !fetchUrl) {
            return undefined;
        }

        let cancelled = false;
        let objectUrl = null;
        const token = localStorage.getItem('access_token');

        fetch(fetchUrl, {
            headers: token ? {Authorization: `Bearer ${token}`} : {},
        })
            .then((res) => (res.ok ? res.blob() : null))
            .then((blob) => {
                if (cancelled) return;
                if (!blob) {
                    setImgFailed(true);
                    return;
                }
                objectUrl = URL.createObjectURL(blob);
                setBlobUrl(objectUrl);
            })
            .catch(() => {
                if (!cancelled) setImgFailed(true);
            });

        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [fetchUrl, needsBlobFetch]);

    const src = React.useMemo(() => {
        if (imgFailed) return undefined;
        if (needsBlobFetch) return blobUrl || undefined;
        return staticSrc || undefined;
    }, [imgFailed, needsBlobFetch, blobUrl, staticSrc]);

    return (
        <Avatar
            {...avatarProps}
            src={src || undefined}
            alt={user?.displayName || user?.email || id || 'User'}
            slotProps={{
                img: {
                    onError: () => setImgFailed(true),
                },
            }}
            sx={{
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                ...sx,
            }}
        >
            {avatarInitials(user, id)}
        </Avatar>
    );
}
